// Victory animation orchestrator.
//
// Plays once on a live win, after the final flick cascade completes and
// before the endgame modal opens. ONE animation: each filled word-bar slot
// rotates 360° once while bouncing (scale 1 → 1.25 → 1) in a single wave
// from the first slot to the last. No reverse pass. Adjacent letters
// overlap slightly (25%) so the ripple feels continuous.
//
// The Dead Letter (the leftover "pusher" tile still on the board at win
// time) does NOT animate — its motion competes with the word being
// celebrated, and the celebration IS the word itself.
//
// Per-slot pulse duration is a fixed value regardless of word length —
// shorter words finish the celebration sooner, longer words take more
// time. Tuned for a deliberate spin feel that doesn't get faster the
// more letters there are.
//
// Reduced-motion users skip the animation entirely (the Promise resolves
// after only POST_ANIMATION_HOLD_MS so the modal still doesn't snap open).

// Each slot's spin + bounce duration during the per-letter cascade. Held
// constant across word lengths; the cascade scales with the number of
// letters.
const PULSE_DURATION_MS = 400

// All-letters-together bounce that fires AFTER the per-letter cascade
// finishes. Scale-only (no rotation) so the word stays readable. Ties the
// individual letters back into a single celebrated word. Runs twice
// back-to-back via CSS animation-iteration-count for emphasis.
const FINALE_BOUNCE_DURATION_MS = 500
const FINALE_BOUNCE_COUNT = 2
const FINALE_TOTAL_MS = FINALE_BOUNCE_DURATION_MS * FINALE_BOUNCE_COUNT

// Hold the all-settled state briefly before opening the endgame modal.
const POST_ANIMATION_HOLD_MS = 400

// Fraction of each per-slot spin that overlaps with the next slot's spin.
// 0.25 → the next letter starts when the previous is 75% through. Tunes
// the "a little bit before the previous finishes" feel.
const OVERLAP_FRACTION = 0.25

// stagger = pulse × (1 − overlap). For pulse 400 ms, overlap 0.25 → 300 ms.
const STAGGER_MS = Math.round(PULSE_DURATION_MS * (1 - OVERLAP_FRACTION))

// A running victory animation. `done` resolves when the celebration finishes
// (or is cancelled). `cancel()` stops it immediately — clears every pending
// timer, strips the victory attributes/styles off the slots, and resolves
// `done` — so a player who navigates to the next puzzle mid-celebration gets an
// instant, clean swap with no leftover spin painting on the new puzzle's tiles.
export interface VictoryAnimationHandle {
  readonly done: Promise<void>
  cancel(): void
}

export function playVictoryAnimation(
  _boardEl: HTMLElement,
  barEl: HTMLElement,
): VictoryAnimationHandle {
  const timers: ReturnType<typeof setTimeout>[] = []
  // Slots we touch, so cancel() can strip any victory state regardless of which
  // pulse/finale timer had fired.
  const touched: HTMLElement[] = []
  let settled = false
  let resolveDone: () => void = (): void => {}
  const done = new Promise<void>((resolve): void => {
    resolveDone = resolve
  })

  const schedule = (fn: () => void, ms: number): void => {
    timers.push(setTimeout(fn, ms))
  }
  const finish = (): void => {
    if (settled) return
    settled = true
    resolveDone()
  }
  const cancel = (): void => {
    if (settled) return
    settled = true
    for (const t of timers) clearTimeout(t)
    for (const slot of touched) {
      slot.removeAttribute('data-victory-pulse')
      slot.removeAttribute('data-victory-finale')
      slot.style.removeProperty('animation-duration')
    }
    resolveDone()
  }

  // Reduced-motion users skip the celebration. Modal still doesn't snap
  // open the instant the win lands — we honor the post-hold so the
  // post-win pacing stays consistent.
  if (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    schedule(finish, POST_ANIMATION_HOLD_MS)
    return { done, cancel }
  }

  const slots = Array.from(
    barEl.querySelectorAll<HTMLElement>('.slot[data-filled="true"]'),
  )

  // No slots (shouldn't happen on a real win, but defensive): hold only.
  if (slots.length === 0) {
    schedule(finish, POST_ANIMATION_HOLD_MS)
    return { done, cancel }
  }
  touched.push(...slots)

  // Single forward pass: slot i pulses at i * STAGGER_MS. Each pulse runs
  // for PULSE_DURATION_MS; the wave ends when the last slot finishes.
  for (let i = 0; i < slots.length; i += 1) {
    const slot = slots[i]
    if (slot === undefined) continue

    schedule((): void => {
      // Inline animation-duration overrides the CSS rule's shorthand for
      // this property only (the rest of the shorthand — name, timing —
      // still applies). Redundant with the CSS value today (both 400ms)
      // but keeps a single source of truth in this file.
      slot.style.animationDuration = `${String(PULSE_DURATION_MS)}ms`
      slot.setAttribute('data-victory-pulse', 'true')
      schedule((): void => {
        slot.removeAttribute('data-victory-pulse')
      }, PULSE_DURATION_MS)
    }, i * STAGGER_MS)
  }

  // After the per-letter cascade finishes, fire the whole-word finale —
  // every slot scales up together and settles back, twice. Scale only;
  // no rotation. Visually ties the letters back into a single word and
  // gives the celebration a deliberate two-beat exclamation point. The
  // CSS rule has animation-iteration-count: 2 baked in, so a single
  // setAttribute drives both bounces.
  const cascadeMs = (slots.length - 1) * STAGGER_MS + PULSE_DURATION_MS
  schedule((): void => {
    for (const slot of slots) {
      // Defensive removeAttribute first: if a per-slot pulse removeAttribute
      // races with the finale setAttribute on the same animation frame,
      // explicit ordering here keeps the state clean.
      slot.removeAttribute('data-victory-pulse')
      slot.style.animationDuration = `${String(FINALE_BOUNCE_DURATION_MS)}ms`
      slot.setAttribute('data-victory-finale', 'true')
      schedule((): void => {
        slot.removeAttribute('data-victory-finale')
      }, FINALE_TOTAL_MS)
    }
  }, cascadeMs)

  // Total scales with N: cascade + finale + post-hold. For N=3 → 2400 ms;
  // N=5 → 3000 ms; N=7 → 3600 ms.
  const totalMs = cascadeMs + FINALE_TOTAL_MS + POST_ANIMATION_HOLD_MS
  schedule(finish, totalMs)

  return { done, cancel }
}
